// LoginScreen — RTLSMobile web sign-in.
//
// Mirrors the TagBoard web LoginScreen visually so a dispatcher who's used
// one immediately recognizes the other. Same Supabase project means the
// same credentials work across both apps.

import React, { useState } from 'react'
import { useAuth } from '../../app/useAuth'

export const LoginScreen: React.FC = () => {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    const result = await signIn(email.trim(), password)
    setBusy(false)
    if (result.error) {
      setError(result.error)
    }
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-background">
      <form
        onSubmit={handleSubmit}
        className="flex w-[400px] flex-col gap-lg rounded-lg border border-surfaceBorder bg-surfaceCard p-xxl"
        aria-label="Sign in"
      >
        <div className="flex items-center gap-md">
          <img
            src="/icons/logo.png"
            alt="RTLS Mobile logo"
            className="h-11 w-11 rounded-md object-contain"
          />
          <div>
            <h1 className="text-xl font-bold text-textPrimary">RTLS Mobile</h1>
            <p className="text-xs text-textMuted">3D mine position tracking</p>
          </div>
        </div>

        <Field
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          placeholder="you@example.com"
        />
        <Field
          label="Password"
          value={password}
          onChange={setPassword}
          type="password"
          placeholder="••••••••"
        />

        {error && (
          <div
            role="alert"
            className="rounded-md border border-statusRed bg-badgeRedBg px-md py-sm text-sm text-statusRed"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !email.trim() || !password}
          className="h-11 rounded-md bg-primary font-bold text-black transition-colors hover:bg-primaryLight disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="text-center text-xs text-textMuted">
          Need an account? Contact your administrator.
        </div>
      </form>
    </div>
  )
}

// Lightweight inline Field — RTLS web has no shared FormField yet.
const Field: React.FC<{
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
}> = ({ label, value, onChange, type = 'text', placeholder }) => (
  <label className="flex flex-col gap-xs">
    <span className="text-sm text-textSecondary">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={type === 'password' ? 'current-password' : 'email'}
      className="h-10 rounded-md border border-surfaceBorder bg-surface px-md text-md text-textPrimary outline-none transition-colors focus:border-primary"
    />
  </label>
)
